import { useSyncExternalStore } from 'react';
import { USER_CACHES } from './sw-routes';

/**
 * The page side of the installed app: registration, the update prompt, persistent storage, the
 * install prompt, online/offline, and the two cache purges.
 *
 * Everything here that could be wrong takes its globals as parameters, because this repo's web
 * tests run under node with no DOM — `captureInstallPrompt` takes an EventTarget, `warmAudio`
 * takes a fetch, `shouldPurgeForLearner` takes two strings. What is left touching `window`
 * directly is registration and the online listeners, which are wiring, not logic.
 */

/** Chrome's install prompt event — not in lib.dom. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface SwState {
  /** A new build is downloaded and waiting. The Shell shows the update line. */
  needRefresh: boolean;
  /** Tell the waiting worker to take over and reload. Null until a worker is registered. */
  updateSW: ((reloadPage?: boolean) => Promise<void>) | null;
  /** `navigator.storage.persist()` result. Null = not asked yet (dev, or no support). */
  persisted: boolean | null;
  /** Stashed `beforeinstallprompt`. Non-null = the App card can offer Install. */
  installPrompt: BeforeInstallPromptEvent | null;
  online: boolean;
}

const initial: SwState = {
  needRefresh: false,
  updateSW: null,
  persisted: null,
  installPrompt: null,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
};

let snapshot: SwState = initial;
const listeners = new Set<() => void>();
let onlineAttached = false;

function emit(patch: Partial<SwState>): void {
  snapshot = { ...snapshot, ...patch };
  for (const l of listeners) l();
}

function attachOnlineListeners(): void {
  if (onlineAttached || typeof window === 'undefined') return;
  onlineAttached = true;
  window.addEventListener('online', () => emit({ online: true }));
  window.addEventListener('offline', () => emit({ online: false }));
}

/** The store's subscribe half. Exported so a test can stand in for a mounted component. */
export function subscribe(l: () => void): () => void {
  attachOnlineListeners();
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

const getSnapshot = (): SwState => snapshot;

/** The whole store — the Me App card and the Shell's update line read this. */
export function useSw(): SwState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Just the connectivity bit — Today and Words show a calm strip when this is false. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribe,
    () => snapshot.online,
    () => true,
  );
}

/** Test seam: the current store value, without a React renderer. */
export function __swSnapshot(): SwState {
  return snapshot;
}

/** Test seam: reset the module store between cases. */
export function __resetSwStore(): void {
  snapshot = { ...initial, online: true };
  listeners.clear();
}

/** How often an open installed app re-checks for a new build. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

/**
 * Register the service worker and wire the update prompt. PROD only — `main.tsx` guards the
 * call, because in dev the Vite server on :5173 proxies /api to the Worker on :8787 and a
 * precached shell there makes every later edit look like it did not land.
 *
 * `virtual:pwa-register` is imported dynamically so this module stays loadable under node.
 */
export async function registerAppSw(): Promise<void> {
  const { registerSW } = await import('virtual:pwa-register');
  const updateSW = registerSW({
    onNeedRefresh() {
      emit({ needRefresh: true });
    },
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // An installed PWA left sitting in Android's recents is never "loaded" again, so without
      // this it only re-checks on the browser's own ~24 h heuristic. Throttled to once an hour:
      // a tab switch should not cost a network round trip.
      let lastCheck = 0;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - lastCheck < UPDATE_CHECK_INTERVAL_MS) return;
        lastCheck = now;
        void registration.update();
      });
    },
  });
  emit({ updateSW });

  // Ask the browser not to evict us under storage pressure. Chrome grants this silently for an
  // installed app; a `false` here is reported honestly on the Me card rather than hidden.
  try {
    const persisted = (await navigator.storage?.persist?.()) ?? null;
    emit({ persisted });
  } catch {
    emit({ persisted: null });
  }
}

/**
 * Stash Chrome's install prompt. Called explicitly from `main.tsx` BEFORE `createRoot` — as an
 * import side effect it would race the first paint and miss the event. `preventDefault()` is
 * what stops Chrome showing its own mini-infobar over the app.
 */
export function captureInstallPrompt(target: EventTarget = window): void {
  target.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    emit({ installPrompt: e as BeforeInstallPromptEvent });
  });
  // Once installed the stashed event is stale — Chrome will not fire it again this session.
  target.addEventListener('appinstalled', () => emit({ installPrompt: null }));
}

/** Show the stashed prompt and clear it however the learner answers. */
export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const e = snapshot.installPrompt;
  if (!e) return 'unavailable';
  try {
    await e.prompt();
    const { outcome } = await e.userChoice;
    return outcome;
  } catch {
    return 'dismissed';
  } finally {
    emit({ installPrompt: null });
  }
}

/** True when the app is running from the home screen rather than a browser tab. */
export function isStandalone(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches
    );
  } catch {
    return false;
  }
}

/**
 * Drop everything cached for the learner who is leaving. Belt and braces on purpose: the page
 * deletes the caches itself AND tells the worker to, because on a first load the worker may not
 * be controlling this page yet and would never hear the message.
 */
export async function purgeUserCaches(): Promise<void> {
  try {
    navigator.serviceWorker?.controller?.postMessage({ type: 'nn:purge' });
  } catch {
    /* no worker: the page-side delete below is the whole job */
  }
  try {
    await Promise.all(USER_CACHES.map((name) => caches.delete(name)));
  } catch {
    /* no CacheStorage (dev, private mode): nothing was cached either */
  }
}

/**
 * Purge when a DIFFERENT learner signs in on this device. A first-ever sign-in does not purge —
 * there is nothing there but this learner's own cold caches — and the same learner returning
 * obviously does not.
 */
export function shouldPurgeForLearner(
  prev: string | null,
  next: string | null | undefined,
): boolean {
  if (!next || !prev) return false;
  return prev !== next;
}

export interface WarmEnv {
  saveData: boolean;
  online: boolean;
}

/** Read the two conditions that call warming off, defensively — both are optional APIs. */
export function readWarmEnv(): WarmEnv {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const conn = (nav as { connection?: { saveData?: boolean } } | undefined)?.connection;
  return { saveData: conn?.saveData === true, online: nav?.onLine !== false };
}

/**
 * Pull saved-word audio through the service worker so it is there in airplane mode. Fetches
 * each URL once, `concurrency` at a time; every error is swallowed because this is a courtesy,
 * never a step the learner is waiting on. Returns how many requests were actually made, which
 * is what the test asserts on.
 */
export async function warmAudio(
  urls: string[],
  fetchImpl: typeof fetch,
  concurrency = 4,
  env: WarmEnv = readWarmEnv(),
): Promise<number> {
  if (!env.online || env.saveData) return 0;
  const queue = [...new Set(urls)];
  let made = 0;
  const worker = async (): Promise<void> => {
    for (;;) {
      const url = queue.shift();
      if (url === undefined) return;
      made += 1;
      try {
        await fetchImpl(url, { credentials: 'include' });
      } catch {
        /* offline mid-warm, or a 404: the next open tries again */
      }
    }
  };
  await Promise.all(Array.from({ length: Math.max(1, concurrency) }, worker));
  return made;
}
